import React, { FunctionComponent, SetStateAction, useContext, useState } from 'react'
import moment from 'moment';
import { Autocomplete, Box, Chip, FormControl, FormHelperText, MenuItem, Select, SelectChangeEvent, Stack, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography, useMediaQuery } from '@mui/material';
import ThirdPartyConnectionImportLineService from '../../../../modules/third-party-connection/shared-services/third-party-connection-import-line/third-party-connection-import-line.service';
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications';
import { ISpendBasedConversion } from '../../../../shared/services/spend-based-conversion/spend-based-conversion.model';
import gaiaTheme from '../../../../root-components/theme';
import { IHeadCell } from '../../../../shared/interfaces/table-display.model';
import { IDiscardImportLine, IFormattedDiscardImportLine, IFormattedImportLine, IFormattedPostImportLine, IPostImportLine, ISwitchableActivitySource } from '../../../../modules/third-party-connection/shared-services/third-party-connection-import-line/third-party-connection-import-line.model';
import { HighlightOff, SaveOutlined } from '@mui/icons-material';
import { TableDisplay } from '../../miscellaneous-components/table-display';
import { IContactSupplierDetails } from '../../../../shared/services/contact-supplier/contact-supplier.model';
import { SupplierPicker } from '../../form-field-components/supplier-picker';
import { NumericFormat } from 'react-number-format';
import { LoadingPage } from '../../../pages/loading-page';
import { LoadingButton } from '@mui/lab';
import { ISubsidiary } from '../../../../shared/services/subsidiary/subsidiary.model';
import { UserDetailsContext } from '../../../../context/user-details/user-details';
import { IGroup } from '../../../../shared/services/group/group.model';
import { sortGroupsByGhgCategory, formatGroupLabel } from '../../../../shared/constants/ghg-category';
import { useIsReportPeriodLocked } from '../../../../hooks/use-is-report-period-locked';
import { ScreenLabelsContext } from '../../../../context/screen-labels/screen-labels';
import { conversionCode, MISSING_CONVERSION_CODE, sortByConversionCode } from '../../../../shared/utils/conversion-utils';
import { accountingProviderName } from '../../../../shared/constants/accounting-providers';
import { SourceLabel } from '../../miscellaneous-components/source-label';
import { segmentedToggleSx } from '../../../../shared/utils/segmented-toggle-sx';

export const EmissionsFinancialOutstandingTable: FunctionComponent<{
  outstandingImportLines: IFormattedImportLine[],
  setOutstandingImportLines: React.Dispatch<SetStateAction<any>>,
  subsidiaries: ISubsidiary[],
  isPendingIntegrationsLoading: boolean,
  inputPage: number,
  setInputPage: React.Dispatch<SetStateAction<any>>,
  totalRecords: number,
  perPage: number,
  setPerPage: React.Dispatch<SetStateAction<any>>,
  getAllThirdPartyConnectionImportLines: (queryString: string) => void,
  selectedSupplier: string,
  setSelectedSupplier: React.Dispatch<SetStateAction<any>>,
  allContactSuppliers: IContactSupplierDetails[],
  spendBasedConversions: ISpendBasedConversion[],
  setOrder: React.Dispatch<SetStateAction<any>>,
  setOrderBy: React.Dispatch<SetStateAction<any>>,
  isCommitSubmitting: boolean,
  setIsCommitSubmitting: React.Dispatch<SetStateAction<boolean>>,
  groups: IGroup[],
  resultYears?: number[],
  // The integrations with pending lines in this period, for the filter; '' selects them all.
  integrationOptions: string[],
  selectedIntegration: string,
  setSelectedIntegration: React.Dispatch<SetStateAction<string>>,
  // Commit all lives on the page, which owns the period and the filters it commits.
  onCommitAll: () => void,
  isCommitAllSubmitting: boolean,
}> = ({
  outstandingImportLines,
  setOutstandingImportLines,
  isPendingIntegrationsLoading,
  subsidiaries,
  inputPage,
  setInputPage,
  totalRecords,
  perPage,
  setPerPage,
  getAllThirdPartyConnectionImportLines,
  selectedSupplier,
  setSelectedSupplier,
  allContactSuppliers,
  spendBasedConversions,
  setOrder,
  setOrderBy,
  isCommitSubmitting,
  setIsCommitSubmitting,
  groups,
  resultYears,
  integrationOptions,
  selectedIntegration,
  setSelectedIntegration,
  onCommitAll,
  isCommitAllSubmitting,
}) => {
    //Service
    const thirdPartyConnectionImportLineService = new ThirdPartyConnectionImportLineService();
    //Context
    const userNotificationsContext = useContext(UserNotificationsContext)
    const userDetailsContext = useContext(UserDetailsContext)
    const organisation = userDetailsContext.userDetails?.organisation
    const screenLabelsContext = useContext(ScreenLabelsContext)
    //State
    const [selected, setSelected] = useState<string[]>([]);
    //Which row's source menu is open, and which row is mid-switch. Keyed by line id so two
    //rows cannot fight over one piece of state.
    const [switchingLineId, setSwitchingLineId] = useState<string | null>(null);
    //Hooks
    const isReportPeriodLocked = useIsReportPeriodLocked()
    //Loading
    const [isDiscardSubmitting, setIsDiscardSubmitting] = useState<boolean>(false)
    //Layout
    const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('md'));

    // Does any line in this batch carry an account code? Drives whether the column appears.
    const hasAnyNominalCode = !!outstandingImportLines?.some(importLine =>
      importLine.nominalCode || importLine.hasMixedAccountCodes);

    const headCells: IHeadCell[] = [
      // ...(!isReportPeriodLocked ? [{ id: 'action', numeric: false, label: 'Action', sortable: false }] : []),
      { id: 'contactName', numeric: false, label: screenLabelsContext.getLabel('supplier', { plural: false, capitalize: 'first' }), sortable: true },
      { id: 'providerCode', numeric: false, label: 'Integration', sortable: true },
      { id: 'group', numeric: false, label: 'Group', sortable: false },
      { id: 'category', numeric: false, label: 'Spend Activity', sortable: false },
      { id: 'confidence', numeric: false, label: 'Activity from', sortable: false },
      // Only shown once the ledger actually gives us codes. Connectors that captured none,
      // and every line imported before capture shipped, would otherwise gain a column of
      // blanks that reads as missing data rather than as not applicable.
      ...(hasAnyNominalCode ? [{ id: 'nominalCode', numeric: false, label: 'Nominal Code', sortable: true }] : []),
      { id: 'total', numeric: true, label: `Amount (${organisation?.currencyCode})`, sortable: true },
      { id: 'date', numeric: false, label: 'Date', sortable: true },
      ...(subsidiaries && (organisation?.enableSubsidiaries || subsidiaries.length > 1) ? [
        { id: 'subsidiary', numeric: false, label: screenLabelsContext.getLabel('subsidiary', { capitalize: 'first' }), sortable: false },
      ] : []),
    ];

    const getApplicableResultYear = (date: string | Date): number | undefined => {
      if (!resultYears || resultYears.length === 0) return undefined;
      if (resultYears.length === 1) return resultYears[0];
      const calendarYear = moment(date).year();
      const sorted = [...resultYears].sort((a, b) => {
        const da = Math.abs(calendarYear - a);
        const db = Math.abs(calendarYear - b);
        if (da !== db) return da - db;
        const aLe = a <= calendarYear ? 0 : 1;
        const bLe = b <= calendarYear ? 0 : 1;
        if (aLe !== bLe) return aLe - bLe;
        return b - a;
      });
      return sorted[0];
    };

    // How confident the name lookup was, which is meaningful only when the name lookup is
    // what decided the line. Fewer Companies House candidates means a less ambiguous name.
    const matchConfidence = (returns: number) => {
      if (returns === 1) return { label: 'High', color: 'success' as const };
      if (returns > 1 && returns < 6) return { label: 'Medium', color: 'warning' as const };
      if (returns > 5 && returns < 11) return { label: 'Low', color: 'warning' as const };
      if (returns > 10) return { label: 'Very Low', color: 'error' as const };
      return null;
    };

    // Which line's switch is in flight, keyed by line id so two rows cannot fight over it.
    const switchSourceHandler = (lineId: string, source: 'NOMINAL_MAPPING' | 'SUPPLIER_DEFAULT') => {
      if (isReportPeriodLocked) {
        userNotificationsContext.openDialog({ active: true, title: 'Action Not Allowed', message: 'The selected report period is locked and cannot be modified', buttonCopy: 'Ok', hideCancelButton: true });
        return;
      }
      setSwitchingLineId(lineId);
      thirdPartyConnectionImportLineService.switchActivitySource(lineId, source)
        .then(() => {
          userNotificationsContext.openSnackbar({
            open: true,
            message: source === 'NOMINAL_MAPPING'
              ? "Switched to this code's mapping. Mapping changes will not overwrite it."
              : "Switched to the supplier's default. Mapping changes will not overwrite it.",
          });
          getAllThirdPartyConnectionImportLines('outstanding');
        })
        .catch(err => {
          console.log(err);
          userNotificationsContext.openSnackbar({
            open: true,
            message: err?.response?.data?.message || 'That line could not be switched, please try again',
            backgroundColor: 'text.primary',
          });
        })
        .finally(() => setSwitchingLineId(null));
    };

    const sourceOptionLabel = (option: ISwitchableActivitySource) => {
      const name = option.source === 'NOMINAL_MAPPING'
        ? `Use nominal code ${option.nominalCode || ''}`.trim()
        : "Use supplier's default";
      return option.activity ? `${name}: ${option.activity}` : name;
    };

    // What decided the line's activity, in the words the dropdown shows and explains.
    const describeActivitySource = (importLine: IFormattedImportLine): { label: string, tooltip: string } => {
      if (importLine.hasMixedAccountCodes) {
        return { label: 'Mixed codes', tooltip: 'This document posts to more than one account, so no single mapping was applied and the supplier default was kept.' };
      }
      switch (importLine.activitySource) {
        case 'NOMINAL_MAPPING':
          return {
            label: importLine.nominalCode ? `Nominal code ${importLine.nominalCode}` : 'Nominal code',
            tooltip: 'A chart of accounts mapping on this code decided the activity.',
          };
        case 'USER':
          return { label: 'Set by you', tooltip: 'Someone chose this activity on this screen. Mapping changes will not overwrite it.' };
        case 'SUPPLIER_DEFAULT':
          return { label: 'Supplier default', tooltip: "Inherited from the supplier's default activity." };
        case 'SUPPLIER_MATCH': {
          const confidence = matchConfidence(importLine.numberOfCompanyHouseReturns);
          return {
            label: confidence ? `Supplier match: ${confidence.label}` : 'Supplier match',
            tooltip: confidence
              ? `Matched from the supplier name. ${confidence.label} confidence: the more companies share this name, the less certain the match.`
              : 'Matched from the supplier name.',
          };
        }
        default:
          // Reached only where provenance is genuinely undeterminable: a line whose nominal
          // mapping and supplier default point at the same activity, so neither can be
          // credited. An empty cell reads as a rendering fault, so it says so instead.
          return {
            label: 'Not recorded',
            tooltip: 'This line was classified before we started recording where the activity came from, or its mapping and supplier default agree so neither can be credited.',
          };
      }
    };

    // A dropdown, so it is plain that the source can be changed: the current answer is what it
    // shows, the other answers for this line are its options, each named by the activity it
    // would apply so the choice is on substance. Disabled when there is nothing to switch to.
    const CURRENT_SOURCE = 'CURRENT';
    const renderActivitySource = (importLine: IFormattedImportLine) => {
      const options = importLine.switchableSources || [];
      const current = describeActivitySource(importLine);
      const disabled = options.length === 0 || isReportPeriodLocked === true || switchingLineId === importLine.id;
      return (
        <Tooltip title={current.tooltip}>
          <FormControl size='small' sx={{ minWidth: '200px' }}>
            <Select
              value={CURRENT_SOURCE}
              disabled={disabled}
              inputProps={{ 'aria-label': 'Activity from' }}
              renderValue={() => current.label}
              onChange={(e: SelectChangeEvent<string>) => {
                if (e.target.value !== CURRENT_SOURCE) switchSourceHandler(importLine.id, e.target.value as 'NOMINAL_MAPPING' | 'SUPPLIER_DEFAULT');
              }}
              fullWidth>
              <MenuItem value={CURRENT_SOURCE} disabled>{current.label}</MenuItem>
              {options.map(option => (
                <MenuItem key={option.source} value={option.source}>{sourceOptionLabel(option)}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Tooltip>
      );
    };
    // The spend source a line resolves against: its supplier's override, else the user's
    // default subsidiary's, else whatever the loaded set has. Mirrors the backend ladder,
    // and is why the page loads every source its lines need rather than just one.
    const defaultSubsidiarySourceId = subsidiaries?.find(subsidiary =>
      subsidiary.id === userDetailsContext.userDetails?.defaultSubsidiaryId)?.defaultSpendBasedConversionSourceId;

    const sourceIdForLine = (importLine: IFormattedImportLine): string | undefined =>
      // The line's own conversion first: a classified line must offer the source it is
      // actually on, or its value cannot be displayed and the row reads as unclassified.
      // Only a line with no factor yet falls back to the supplier and subsidiary ladder.
      importLine.spendBasedConversion?.conversionSourceId
      || allContactSuppliers?.find(supplier => supplier.id === importLine.contactSupplierId)
        ?.defaultSpendBasedConversionSourceId
      || defaultSubsidiarySourceId
      || undefined;

    const rows = !outstandingImportLines ? [] : outstandingImportLines.map(importLine => {
      const applicableYear = getApplicableResultYear(importLine.date);
      const rowSourceId = sourceIdForLine(importLine);
      const conversionsForRow = sortByConversionCode(
        spendBasedConversions?.filter(conversion =>
          (applicableYear ? conversion.calendarYear === applicableYear : true)
          // Offer only this line's own source. Without it a row could be given an activity
          // from a source its supplier does not resolve against, which commits a factor the
          // backend would never have chosen for it.
          && (!rowSourceId || conversion.conversionSourceId === rowSourceId))
      );
      return {
        id: importLine.id,
        contactName: importLine.contactName,
        providerCode: <SourceLabel providerCode={importLine.providerCode} />,
        group: (
          <FormControl sx={{ minWidth: '260px' }}>
            <Select
              id='groupId' name='groupId'
              size='small'
              value={importLine.groupId}
              error={importLine.hasTableGroupError}
              disabled={isReportPeriodLocked === true}
              onChange={(e: SelectChangeEvent<string>) => {
                editSpendBasedImportLine(importLine.id, 'groupId', e.target.value);
              }}
              fullWidth>
              {sortGroupsByGhgCategory(groups).map((group) => (
                <MenuItem key={group.id}
                  value={group.id}>{formatGroupLabel(group)}</MenuItem>
              ))}
            </Select>
            {importLine.hasTableGroupError && <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{importLine.groupErrorMessage}</FormHelperText>}
          </FormControl>
        ),
        category: spendBasedConversions &&
          (
            <FormControl sx={{ minWidth: '250px' }}>
              <Autocomplete
                id='spendBasedConversionId'
                size='small'
                fullWidth
                disabled={isReportPeriodLocked === true}
                options={conversionsForRow}
                autoHighlight
                getOptionLabel={(option) => `${option.activity ?? ''}`}
                filterOptions={(options, { inputValue }) => {
                  const searchTerm = inputValue.toLowerCase();
                  return options.filter((option) =>
                    (option.activity ?? '').toLowerCase().includes(searchTerm) ||
                    conversionCode(option).toLowerCase().includes(searchTerm)
                  );
                }}
                renderOption={(props, option) => {
                  const { key, ...restProps } = props as { key: React.Key } & React.HTMLAttributes<HTMLLIElement>;
                  return (
                    <Box
                      component="li"
                      sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
                      key={key}
                      {...restProps}>
                      {conversionCode(option) || MISSING_CONVERSION_CODE}: {option.activity}
                    </Box>
                  )
                }}
                value={spendBasedConversions?.find(conversion => conversion.id === importLine.spendBasedConversionId)}
                onChange={(e: any, value: ISpendBasedConversion | null) => editImportLineSpendBasedConversion(e, importLine.id, value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    error={importLine.hasTableCategoryError}
                    placeholder="Please select a category"
                  />
                )}
              />
              {importLine.hasTableCategoryError && <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{importLine.categoryErrorMessage}</FormHelperText>}
            </FormControl>
          ),
        // Provenance, not a confidence grade. The old column counted Companies House
        // matches, which only ever described the name-lookup case, and badged any supplier
        // carrying a default as "User Verified" including ones assigned automatically with
        // nobody looking. It overclaimed on nearly every row and said nothing at all about
        // lines a nominal mapping had decided.
        //
        // The match-confidence grade survives inside SUPPLIER_MATCH, the one case it
        // genuinely describes.
        confidence: renderActivitySource(importLine),
        // A document posting to several expense accounts cannot take any one code's mapping,
        // so it keeps the supplier default and says so. The tooltip lists the split rather
        // than making the reviewer open the document in the ledger to find out.
        nominalCode: importLine.hasMixedAccountCodes ? (
          <Tooltip title={
            <Box>
              <Typography variant='caption' component='div' sx={{ mb: 0.5 }}>
                This document posts to more than one account, so no single mapping was applied:
              </Typography>
              {(importLine.accountCodeBreakdown || []).map(entry => (
                <Typography variant='caption' component='div' key={entry.code}>
                  {entry.code}{entry.name ? ` ${entry.name}` : ''}: {entry.amount.toLocaleString()}
                </Typography>
              ))}
            </Box>
          }>
            <Chip variant='outlined' color='warning' size='small' label='Mixed codes' />
          </Tooltip>
        ) : (
          <Typography variant='body2'>{importLine.nominalCode || ''}</Typography>
        ),
        date: moment(importLine.date).format('DD/MM/YYYY'),
        total: (
          <>
            < NumericFormat value={importLine.total} displayType={'text'} thousandSeparator="," decimalScale={2} fixedDecimalScale />
            {/* A document in another currency: say what it was converted from, and on what basis,
                in the same words as the record page. */}
            {importLine.originalCurrencyCode && importLine.currencyCode && importLine.originalCurrencyCode !== importLine.currencyCode && importLine.exchangeRate != null && (
              <Stack alignItems='flex-end' gap={0.5} sx={{ mt: 0.5 }}>
                <Typography variant='caption' sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                  <NumericFormat value={importLine.originalTotalInBaseCurrency ?? undefined} displayType={'text'} thousandSeparator="," decimalScale={2} fixedDecimalScale /> {importLine.originalCurrencyCode} at 1 {importLine.originalCurrencyCode} = {new Intl.NumberFormat('en-GB', { maximumFractionDigits: 6 }).format(importLine.exchangeRate)} {importLine.currencyCode}
                </Typography>
                {importLine.exchangeRateBasis === 'PERIOD_FIXED'
                  ? <Chip label='Fixed for this reporting period' color='info' size='small' />
                  : <Chip label={`Daily market rate on ${moment(importLine.date).format('DD/MM/YYYY')}`} size='small' />}
              </Stack>
            )}
          </>
        ),
        subsidiary: (
          <FormControl sx={{ minWidth: '175px' }}>
            <Select
              disabled={isReportPeriodLocked === true}
              id='subsidiaryId' name='subsidiaryId'
              size='small'
              value={importLine.subsidiaryId}
              error={importLine.hasTableSubsidiaryError}
              onChange={(e: SelectChangeEvent<string>) => {
                editSpendBasedImportLine(importLine.id, 'subsidiaryId', e.target.value);
              }}
              fullWidth>
              {subsidiaries?.sort((a, b) => a.name.localeCompare(b.name)).map((subsidiary) => (
                <MenuItem key={subsidiary.id}
                  value={subsidiary.id}>{subsidiary.name}</MenuItem>
              ))}
            </Select>
            {importLine.hasTableSubsidiaryError && <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{importLine.subsidiaryErrorMessage}</FormHelperText>}
          </FormControl>
        ),
        // action: !isReportPeriodLocked ? (
        //   <TableActionButtons
        //     id={importLine.id}
        //     includeEdit={true}
        //     includeDelete={true}
        //     onEdit={(id: string) => saveHandler(id)}
        //     onDelete={(id: string) => deleteHandler(id)}
        //     disableButton={outstandingImportLines.filter(line => line.isSelected).length > 0}
        //   />
        // ) : null,
        hasTableError: importLine.hasTableError,
        tableErrorMesage: importLine.tableErrorMessage,
      }
    })


    const handleOrderChange = (orderBy: string, order: 'asc' | 'desc',) => {
      setOrderBy(orderBy)
      setOrder(order)
    };

    const editImportLineSpendBasedConversion = (
      e: SelectChangeEvent<ISpendBasedConversion | null>,
      lineId: string,
      selectedSpendBasedConversion: ISpendBasedConversion | null
    ) => {
      if (isReportPeriodLocked) {
        userNotificationsContext.openDialog({ active: true, title: 'Action Not Allowed', message: `The selected report period is locked and cannot be modified`, buttonCopy: 'Ok', hideCancelButton: true })
      } else {
        let requiredLine = outstandingImportLines.find((line) => line.id === lineId);
        if (requiredLine && selectedSpendBasedConversion) {
          const selectedContactSupplier = allContactSuppliers.find((supplier) => supplier.name === requiredLine!.contactName);
          if (selectedContactSupplier && selectedContactSupplier.spendBasedConversionId === null) {
            userNotificationsContext
              .openDialog({
                active: true,
                title: "Confirmation",
                message: `Would you like to set ${selectedSpendBasedConversion.activity} as the default spend activity for ${selectedContactSupplier.name}?`,
                buttonCopy: "Yes",
                cancelButtonCopy: "No",
              })
              .then((response) => {
                if (response) {
                  editSpendBasedImportLine(requiredLine!.id, 'spendBasedConversionId', selectedSpendBasedConversion.id, { selectedByUser: true, updateSupplier: true });
                } else {
                  editSpendBasedImportLine(requiredLine!.id, 'spendBasedConversionId', selectedSpendBasedConversion.id, { selectedByUser: true, updateSupplier: false });
                }
              })
              .catch((err) => console.log(err));
          } else {
            editSpendBasedImportLine(requiredLine.id, 'spendBasedConversionId', selectedSpendBasedConversion.id, { selectedByUser: true, updateSupplier: false });
          }
        }
      }
    };

    const editSpendBasedImportLine = (lineId: string, fieldName: string, value: string, extraFields: Record<string, any> = {}) => {
      setOutstandingImportLines((prevLines: any) =>
        prevLines.map((line: any) =>
          line.id === lineId ? { ...line, [fieldName]: value, ...extraFields } : line
        )
      );
      thirdPartyConnectionImportLineService.editImportLine(lineId, { [fieldName]: value, ...extraFields })
        .then(() => {
          // Refetch rather than reload the page: the filters and the selection stay as they were.
          getAllThirdPartyConnectionImportLines('outstanding')
          userNotificationsContext.openSnackbar({ open: true, message: 'Updated successfully' })
        })
        .catch(err => {
          if (err.response.data.code === 1) {
            userNotificationsContext.openSnackbar({ open: true, message: 'Report period is locked', backgroundColor: 'text.primary' });
          } else {
            userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
          }
          console.log(err)
        })
    };

    const deleteHandler = (lineId?: string) => {
      if (isReportPeriodLocked) {
        userNotificationsContext.openDialog({ active: true, title: 'Action Not Allowed', message: `The selected report period is locked and cannot be modified`, buttonCopy: 'Ok', hideCancelButton: true })
      } else {
        userNotificationsContext.openDialog({ active: true, title: 'Confirmation', message: lineId ? `Are you sure you want to delete this record? This action cannot be undone.` : `Are you sure you want to delete these records? This action cannot be undone.`, buttonCopy: 'Continue', cancelButtonCopy: 'Cancel' })
          .then(response => {
            if (response) {
              if (!isDiscardSubmitting) {
                const postData: IDiscardImportLine[] = outstandingImportLines
                  .filter(line => lineId ? line.id === lineId : selected.includes(line.id))
                  .map(line => ({ id: line.id }));
                const formattedPostData: IFormattedDiscardImportLine = {
                  discarded: postData
                }
                setIsDiscardSubmitting(true)
                thirdPartyConnectionImportLineService.discardImportLine(formattedPostData)
                  .then(response => {
                    userNotificationsContext.openSnackbar({ open: true, message: `Discarded successfully` })
                    setSelected([])
                    if (inputPage > 0 && outstandingImportLines && (outstandingImportLines.length === 1 || formattedPostData.discarded.length === outstandingImportLines.length)) {
                      setInputPage(inputPage - 1)
                    } else {
                      getAllThirdPartyConnectionImportLines('outstanding')
                    }
                    setIsDiscardSubmitting(false)
                  })
                  .catch(err => {
                    if (err.response.data.code === 1) {
                      userNotificationsContext.openSnackbar({ open: true, message: 'Report period is locked', backgroundColor: 'text.primary' });
                    } else {
                      userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
                    }
                    console.log(err)
                    setIsDiscardSubmitting(false)
                  })
              }
            }
          })
      }
    }

    const saveHandler = async (lineId?: string) => {
      if (isReportPeriodLocked) {
        userNotificationsContext.openDialog({ active: true, title: 'Action Not Allowed', message: `The selected report period is locked and cannot be modified`, buttonCopy: 'Ok', hideCancelButton: true })
      } else {
        let errorCount = 0;
        const linesToValidate = outstandingImportLines.filter(line => lineId ? line.id === lineId : selected.includes(line.id));
        const formatted = outstandingImportLines.map((line) => {
          const isLineToValidate = linesToValidate.includes(line);
          let hasTableCategoryError = false;
          let hasTableSubsidiaryError = false;
          let hasTableGroupError = false;
          let subsidiaryErrorMessage = '';
          let categoryErrorMessage = '';
          let groupErrorMessage = '';

          if (isLineToValidate) {
            if (!line.subsidiaryId) {
              hasTableSubsidiaryError = true;
              subsidiaryErrorMessage = `You must enter a ${screenLabelsContext.getLabel('subsidiary', { plural: false })} for this record`;
              errorCount++;
            }

            if (!line.spendBasedConversionId) {
              hasTableCategoryError = true;
              categoryErrorMessage = 'You must enter a category for this record';
              errorCount++;
            }

            if (!line.groupId) {
              hasTableGroupError = true;
              groupErrorMessage = 'You must enter a group for this record';
              errorCount++;
            }
          }

          return {
            ...line,
            hasTableError: hasTableSubsidiaryError || hasTableCategoryError || hasTableGroupError,
            tableErrorMessage: `${subsidiaryErrorMessage} ${categoryErrorMessage} ${groupErrorMessage}`.trim(),
            hasTableSubsidiaryError,
            subsidiaryErrorMessage,
            hasTableCategoryError,
            categoryErrorMessage,
            hasTableGroupError,
            groupErrorMessage,
          };
        });
        setOutstandingImportLines(formatted);
        if (errorCount === 0) {
          if (lineId) {
            submitHandler(lineId)
          } else {
            submitHandler()
          }
        }
      }
    }

    const submitHandler = (lineId?: string) => {
      if (outstandingImportLines) {
        const postData: IPostImportLine[] = outstandingImportLines.filter((line) => lineId ? line.id === lineId : selected.includes(line.id)).map((line) => {
          return {
            id: line.id,
            spendBasedConversionId: line.spendBasedConversionId,
            subsidiaryId: line.subsidiaryId,
            groupId: line.groupId,
          }
        })
        const formattedPostData: IFormattedPostImportLine = {
          committed: postData
        }
        if (!isCommitSubmitting) {
          setIsCommitSubmitting(true)
          thirdPartyConnectionImportLineService.createImportLine(formattedPostData)
            .then(() => {
              userNotificationsContext.openSnackbar({ open: true, message: `Saved successfully` })
              setIsCommitSubmitting(false)
              setSelected([])
              if (inputPage > 0 && outstandingImportLines && (outstandingImportLines.length === 1 || formattedPostData.committed.length === outstandingImportLines.length)) {
                setInputPage(inputPage - 1)
              } else {
                getAllThirdPartyConnectionImportLines('outstanding')
              }
            })
            .catch((err: any) => {
              if (err.response.data.code === 1) {
                userNotificationsContext.openSnackbar({ open: true, message: 'Report period is locked', backgroundColor: 'text.primary' });
              } else {
                userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
              }
              console.log(err)
              setIsCommitSubmitting(false)
            })
        }
      }
    }

    return (
      <>
        <Box display='flex' sx={{ flexDirection: isMobileMode ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobileMode ? 'flex-start' : 'center', gap: 2, mb: 2 }}>
          <Stack direction={isMobileMode ? 'column' : 'row'} alignItems={isMobileMode ? 'stretch' : 'center'} gap={2} sx={{ width: isMobileMode ? '100%' : 'auto' }}>
            <FormControl variant='outlined' sx={{ width: isMobileMode ? '100%' : '260px' }} size='small'>
              {/* Cleared means all suppliers. */}
              <SupplierPicker
                id='status'
                suppliers={allContactSuppliers}
                label='Filter'
                placeholder='All'
                size='small'
                value={selectedSupplier || null}
                onChange={(id) => setSelectedSupplier(id || '')}
              />
            </FormControl>
            {/* Only the integrations with lines in this period are offered; one alone needs no filter. */}
            {integrationOptions.length > 1 && (
              <ToggleButtonGroup
                value={selectedIntegration || 'all'}
                exclusive
                onChange={(_event, newValue) => {
                  if (newValue !== null) setSelectedIntegration(newValue === 'all' ? '' : newValue)
                }}
                size='small'
                aria-label='Integration'
                sx={segmentedToggleSx}
              >
                <ToggleButton value='all' aria-label='All integrations'>All</ToggleButton>
                {integrationOptions.map(code => (
                  <ToggleButton key={code} value={code} aria-label={accountingProviderName(code)}>{accountingProviderName(code)}</ToggleButton>
                ))}
              </ToggleButtonGroup>
            )}
          </Stack>
          {!isReportPeriodLocked && <Stack sx={{ display: 'flex', flexDirection: isMobileMode ? 'column' : 'row', justifyContent: 'flex-end', width: isMobileMode ? '100%' : 'auto' }}>
            <LoadingButton
              onClick={() => deleteHandler()}
              disabled={selected.length === 0 || isCommitSubmitting || isDiscardSubmitting || isCommitAllSubmitting}
              startIcon={<HighlightOff />}
              variant='outlined'
              loading={isDiscardSubmitting}
              loadingPosition="start"
              sx={{ mr: isMobileMode ? 0 : 2, mb: isMobileMode ? 2 : 0 }}>
              <Typography>{isDiscardSubmitting ? 'Discarding Selected' : 'Discard Selected'}</Typography>
            </LoadingButton>
            <LoadingButton
              onClick={() => saveHandler()}
              disabled={selected.length === 0 || isCommitSubmitting || isDiscardSubmitting || isCommitAllSubmitting}
              startIcon={<SaveOutlined />}
              variant='contained'
              loading={isCommitSubmitting}
              loadingPosition="start"
              sx={{ mr: isMobileMode ? 0 : 2, mb: isMobileMode ? 2 : 0 }}>
              <Typography>{isCommitSubmitting ? 'Committing Selected' : 'Commit Selected'}</Typography>
            </LoadingButton>
            {/* Everything ready in this view (period, integration and supplier filters), selected server side. */}
            <LoadingButton
              onClick={onCommitAll}
              disabled={totalRecords === 0 || isCommitSubmitting || isDiscardSubmitting || isCommitAllSubmitting}
              startIcon={<SaveOutlined />}
              variant='contained'
              loading={isCommitAllSubmitting}
              loadingPosition="start">
              <Typography>{isCommitAllSubmitting ? 'Committing All' : 'Commit All'}</Typography>
            </LoadingButton>
          </Stack>}
        </Box>
        <LoadingPage pageReady={!isPendingIntegrationsLoading}>
          <TableDisplay
            headCells={headCells}
            rows={rows}
            selected={selected}
            onOrderChange={handleOrderChange}
            setSelected={setSelected}
            haveCheckboxSelect={isReportPeriodLocked ? false : true}
            selectByCheckboxOnly={true}
            inputPage={inputPage}
            setInputPage={setInputPage}
            totalRecords={totalRecords}
            perPage={perPage}
            setPerPage={setPerPage} />
        </LoadingPage>
      </>)
  }